import { useContext, useState } from 'react';

import { analyticsProps } from '@class-ui/insights';
import { Button, Stack, styled, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import dateFormat from 'date-fns-tz/format';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { EditSessionDialog, type EditSessionDialogProps } from './EditSessionDialog';
import { NoRecords } from './SharedComponents';
import { getGroupsForSession } from './util';
import { ClassContext } from '../../context/ClassContext';
import { type ClassSessionInfo, type ClassStudentGroup } from '../../context/types';
import { type OverridableClassOptions } from '../../types/adminConfig';
import { type ClassUserSessionData } from '../../types/apiTypes';
import { StyledActionButton } from '../../views/AdminSettings/styles/table-styles';
import Loadable from '../common/loadable';

const ActionsBox = styled(Stack)`
    flex-direction: row;
    align-items: center;
    gap: 4px;
`;

const TableCellTime = styled(TableCell)`
    width: auto;
    max-width: 200px;
`;

const TableCellTopic = styled(TableCell)`
    width: auto;
    min-width: 100px;
    max-width: 800px;
`;

const TableCellLabel = styled(TableCell)`
    min-width: 100px;
    max-width: 200px;
    width: auto;
`;

const TableCellSections = styled(TableCell)`
    min-width: 100px;
    width: auto;
`;

const TableCellAction = styled(TableCell)`
    width: auto;
`;

const TableCellNoWrap = styled(TableCell)`
    white-space: nowrap;
`;

export function sessionOpened(
    classSettings: OverridableClassOptions,
    isPrivilegedUser: boolean,
    session?: ClassSessionInfo | ClassUserSessionData
): boolean {
    if (!session) {
        return false;
    }

    const now = new Date().getTime();
    const allowUserJoinPriorMinutes = isPrivilegedUser
        ? classSettings.allowInstructorAutoJoinPriorMinutes
        : classSettings.allowStudentJoinPriorMinutes;
    const allowUserJoinPriorMillis = allowUserJoinPriorMinutes * 60 * 1000;
    return (
        new Date(Number(session.start_time) * 1000).getTime() - allowUserJoinPriorMillis <= now &&
        new Date(Number(session.end_time) * 1000).getTime() >= now
    );
}

const buildResult = (id: string, classHasGroups: boolean, label?: string, section?: string) => (
    <>
        <TableCell key={`label-${id}`} {...analyticsProps(`session-row-label: ${label}`)}>
            {label && <Typography>{label}</Typography>}
        </TableCell>
        {classHasGroups && (
            <TableCell key={`section-${id}`} {...analyticsProps(`session-row-section: ${section}`)}>
                {section && <Typography>{section}</Typography>}
            </TableCell>
        )}
    </>
);

type ConditionalTableCellsProps = {
    id: string;
    session: ClassSessionInfo;
    classGroups: ClassStudentGroup[];
    myGroups: ClassStudentGroup[];
    showSections: boolean;
    role?: string;
};

const ConditionalTableCells = (props: ConditionalTableCellsProps) => {
    const { id, session, classGroups, myGroups, showSections, role } = props;

    const { t } = useTranslation(['class']);

    if (!session.class_session_group) {
        // empty result
        return buildResult(id, showSections);
    }

    const { label, selectedGroups: sessionAssignedGroupIds = [] } = session.class_session_group.config;

    if (!showSections) {
        return buildResult(id, false, label);
    }

    if (sessionAssignedGroupIds.includes('all')) {
        return buildResult(id, true, label, t('class:all-sections'));
    }

    const groups = getGroupsForSession(session, classGroups, myGroups, role);
    const selectedGroupLabels: string[] = groups.map((g) => g.label);

    return buildResult(id, true, label, selectedGroupLabels.join(', '));
};

type SessionsTableProps = {
    id: string;
    role?: string;
    userGroups: ClassStudentGroup[];
    classGroups: ClassStudentGroup[];
    sessions: ClassSessionInfo[] | undefined;
    className: string;
    isInstructor: boolean;
    isGuest: boolean;
    showingPastSessionsOnly?: boolean;
    classSettings: OverridableClassOptions;
    isPrivilegedUser: boolean;
    showSections: boolean;
    onClickLaunch?: () => void;
    onClickSchedule?: () => void;
};

export const SessionsTable = (props: SessionsTableProps) => {
    const {
        id,
        role,
        userGroups,
        classGroups,
        sessions,
        isInstructor,
        isGuest,
        showingPastSessionsOnly,
        showSections,
        onClickSchedule,
        onClickLaunch,
        classSettings,
        isPrivilegedUser
    } = props;

    const { t } = useTranslation(['class']);
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const { isLoadingClassData } = useContext(ClassContext);

    const classHasGroups: boolean = !!classGroups?.length;

    const [editSessionDialogProps, setEditSessionDialogProps] = useState<EditSessionDialogProps>({
        open: false,
        session: undefined
    });

    const onClickEdit = (session: ClassSessionInfo) => {
        setEditSessionDialogProps((current) => ({ ...current, open: true, session }));
    };
    const onClickDetails = (session: ClassSessionInfo) => {
        window.location.href = `/react/lti/${session.class_uuid}/session/${
            session.class_session_uuid
        }?${searchParams.toString()}`;
    };

    return (
        <Loadable fullScreen={true} isLoading={isLoadingClassData}>
            <EditSessionDialog
                {...editSessionDialogProps}
                onClose={() => setEditSessionDialogProps((current) => ({ ...current, open: false }))}
            />

            <Table>
                <TableHead>
                    <TableRow key={`header-${id}`}>
                        <TableCellTime>{t('class:time')}</TableCellTime>
                        <TableCellTopic>{t('class:topic')}</TableCellTopic>
                        <TableCellLabel>{t('class:label')}</TableCellLabel>
                        {classHasGroups && showSections && <TableCellSections>{t('class:sections')}</TableCellSections>}
                        <TableCellAction>{t('class:actions')}</TableCellAction>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {sessions?.map((session) => (
                        <TableRow key={`${id}-${session.class_session_uuid}`}>
                            <TableCellNoWrap key={`date-${id}-${session.class_session_uuid}`}>
                                {dateFormat(new Date(Number(session.start_time) * 1000), 'PPPP')}
                                <br />
                                {dateFormat(new Date(Number(session.start_time) * 1000), 'p')} -{' '}
                                {dateFormat(new Date(Number(session.end_time) * 1000), 'p')}
                            </TableCellNoWrap>

                            <TableCell
                                key={`topic-${id}-${session.class_session_uuid}`}
                                {...analyticsProps(`session-row-topic: ${session.class_session_topic}`)}
                            >
                                {session.class_session_topic}
                            </TableCell>

                            <ConditionalTableCells
                                id={`${id}-${session.class_session_uuid}`}
                                session={session}
                                classGroups={classGroups}
                                myGroups={userGroups}
                                showSections={showSections && classHasGroups}
                                role={role}
                            />

                            <TableCell key={`action-${id}-${session.class_session_uuid}`}>
                                <ActionsBox>
                                    {isInstructor && (
                                        <StyledActionButton
                                            size="medium"
                                            color="primary"
                                            variant="outlined"
                                            onClick={() => onClickEdit(session)}
                                            {...analyticsProps(`session-button-edit`)}
                                        >
                                            {t('class:edit')}
                                        </StyledActionButton>
                                    )}

                                    {!isGuest && sessionOpened(classSettings, isPrivilegedUser, session) && (
                                        <StyledActionButton
                                            size="medium"
                                            color="primary"
                                            variant="contained"
                                            onClick={onClickLaunch}
                                            {...analyticsProps(`session-button-launch`)}
                                        >
                                            {t('class:launch')}
                                        </StyledActionButton>
                                    )}
                                    <StyledActionButton
                                        size="medium"
                                        color="primary"
                                        variant="outlined"
                                        onClick={() => onClickDetails(session)}
                                        {...analyticsProps(`session-button-details`)}
                                    >
                                        {t('class:session-details-button')}
                                    </StyledActionButton>
                                </ActionsBox>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            {!sessions?.length && (
                <NoRecords
                    message={
                        showingPastSessionsOnly ? t('class:no-past-sessions-found') : t('class:no-sessions-scheduled')
                    }
                >
                    {isInstructor && !showingPastSessionsOnly && (
                        <Button
                            sx={{ mt: 1 }}
                            size="medium"
                            color="primary"
                            variant="contained"
                            onClick={onClickSchedule}
                            {...analyticsProps('session-button-schedule')}
                        >
                            {t('class:schedule-session')}
                        </Button>
                    )}
                </NoRecords>
            )}
        </Loadable>
    );
};
